import { useEffect, useMemo } from 'react';
import { business, serviceAreas, services } from './siteData';

const setMetaTag = (attribute, key, content) => {
  if (!content) {
    return;
  }

  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const setCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

export const buildLocalBusinessSchema = (path = '/') => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: business.name,
    description:
      'KMK Drywall provides residential drywall, commercial drywall, drywall finishing, steel framing, ceiling tile grid, and drywall repair in Springville, Buffalo, Erie County, and Western New York.',
    telephone: business.phoneSchema,
    email: business.email,
    url: origin ? `${origin}${normalizedPath}` : undefined,
    address: {
      '@type': 'PostalAddress',
      addressLocality: business.city,
      addressRegion: business.region,
      addressCountry: 'US',
    },
    areaServed: serviceAreas.map((area) => ({
      '@type': 'Place',
      name: `${area}, NY`,
    })),
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Drywall and Interior Contractor Services',
      itemListElement: services.map((service) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: service.title,
          areaServed: business.serviceArea,
        },
      })),
    },
  };
};

const SEO = ({ title, description, path = '/', schema }) => {
  const canonicalUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return path;
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${window.location.origin}${normalizedPath}`;
  }, [path]);

  useEffect(() => {
    document.title = title;
    setMetaTag('name', 'description', description);
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setCanonical(canonicalUrl);

    const oldSchema = document.getElementById('local-business-schema');
    if (oldSchema) {
      oldSchema.remove();
    }

    if (schema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'local-business-schema';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [canonicalUrl, description, schema, title]);

  return null;
};

export default SEO;
